import React, { useState } from 'react';
import { portfolioImages } from '../data/mock';
import { X, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const INITIAL_COUNT = 6;

const PortfolioSection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('Alle');
  const [showAll, setShowAll] = useState(false);

  const categories = ['Alle', 'Campaign', 'Product', 'On-Model', 'E-Commerce', 'Social / UGC', 'Editorial'];

  const filteredImages = filter === 'Alle'
    ? portfolioImages
    : portfolioImages.filter(img => img.category === filter);

  // When filtering by category, show all. When "Alle" and not expanded, show first 6
  const displayedImages = (filter !== 'Alle' || showAll)
    ? filteredImages
    : filteredImages.slice(0, INITIAL_COUNT);

  const hasMore = filter === 'Alle' && !showAll && filteredImages.length > INITIAL_COUNT;

  const openLightbox = (image) => setSelectedImage(image);
  const closeLightbox = () => setSelectedImage(null);

  const navigateImage = (direction) => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    if (direction === 'next') {
      newIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    }
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-12 lg:py-20 relative">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#88a2ff]/4 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-12">
          <span className="inline-block px-4 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-full text-sm text-gray-400 mb-4 lg:mb-5 tracking-wide">
            Portfolio
          </span>
          <h2 className="text-2xl lg:text-4xl font-bold mb-3 lg:mb-4 text-white">
            Ausgewählte <span className="text-gradient">Arbeiten</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm lg:text-base leading-relaxed">
            Realistische Fashion- und Produktvisuals für Kampagnen, E-Commerce, Social und Ads.
          </p>
        </div>

        {/* Filter Tabs - only show when expanded or filtering */}
        {(showAll || filter !== 'Alle') && (
          <div className="flex justify-center gap-2 mb-8 lg:mb-10 flex-wrap">
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`portfolio-filter-${category}`}
                onClick={() => setFilter(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  filter === category
                    ? 'bg-[#ff84e4] text-black'
                    : 'bg-white/[0.04] text-gray-500 hover:bg-white/[0.08] hover:text-gray-300 border border-white/[0.06]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayedImages.map((image) => (
            <div
              key={image.id}
              data-testid={`portfolio-item-${image.id}`}
              onClick={() => openLightbox(image)}
              className={`group relative overflow-hidden cursor-pointer ${
                image.wide ? 'md:col-span-2' : ''
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <span className="inline-block px-3 py-1 bg-white/10 backdrop-blur-sm text-white text-xs font-medium rounded-full mb-2 border border-white/10">
                  {image.category}
                </span>
                <p className="text-white text-sm font-medium">{image.alt}</p>
              </div>
            </div>
          ))}
        </div>

        {/* "Mehr ansehen" Button */}
        {hasMore && (
          <div className="text-center mt-10">
            <button
              data-testid="portfolio-show-more"
              onClick={() => setShowAll(true)}
              className="group inline-flex items-center gap-2 px-8 py-3.5 border border-white/15 text-white font-semibold rounded-full transition-colors duration-200 hover:bg-white/[0.04] hover:border-white/25 text-sm"
            >
              Mehr ansehen
              <ChevronDown size={18} className="transition-transform group-hover:translate-y-0.5" />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 p-2 text-white/60 hover:text-white transition-colors"
          >
            <X size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
            className="absolute left-4 p-3 text-white/60 hover:text-white transition-colors bg-white/10 rounded-full hover:bg-white/20"
          >
            <ChevronLeft size={28} />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
            className="absolute right-4 p-3 text-white/60 hover:text-white transition-colors bg-white/10 rounded-full hover:bg-white/20"
          >
            <ChevronRight size={28} />
          </button>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;
